/**
 * Shield Bearer Renderer - Specialized renderer for shield bearer units with attack animation
 * Features:
 * - Procedural attack animation via custom shaders
 * - Spear thrust attack motion
 * - Shield defensive stance
 * - Body movement and leg animation
 */

import * as THREE from 'three';
import type { FactionId, Entity } from '@/types';
import { EventBus } from '@/core/events/EventBus';

const MAX_SHIELD_BEARERS = 500;

// Animation group IDs encoded in vertex attribute
export const SHIELD_ANIM_GROUP = {
  STATIC: 0,       // No animation
  BODY: 1,         // Body - lean forward during attack
  HEAD: 2,         // Head - follows body
  LEFT_ARM: 3,     // Left arm (holding shield)
  RIGHT_ARM: 4,    // Right arm (holding spear)
  LEFT_LEG: 5,     // Left leg
  RIGHT_LEG: 6,    // Right leg
  SHIELD: 7,       // Shield - raise during attack
  SPEAR: 8,        // Spear - thrust forward
  SHOULDER: 9,     // Shoulder armor - follows body
};

// Color palette
const COLORS = {
  skin: new THREE.Color(0xf5deb3),
  gold: new THREE.Color(0xdaa520),
  bronze: new THREE.Color(0xcd7f32),
  steel: new THREE.Color(0xa8a8a8),
  iron: new THREE.Color(0x71797E),
  shieldBrown: new THREE.Color(0x8B6914),
  tassel: new THREE.Color(0xcc2222),
  wood: new THREE.Color(0x8b4513),
  woodDark: new THREE.Color(0x5d4037),
  markerPrimary: new THREE.Color(1.0, 0.1, 0.1),
  markerSecondary: new THREE.Color(0.1, 1.0, 0.1),
};

// Faction colors
const FACTION_COLORS: Record<string, { primary: THREE.Color; secondary: THREE.Color }> = {
  yan: { primary: new THREE.Color(0xc73e3a), secondary: new THREE.Color(0xd4a843) },
  han: { primary: new THREE.Color(0x5b9bd5), secondary: new THREE.Color(0xe8e8e8) },
  lin: { primary: new THREE.Color(0x4caf50), secondary: new THREE.Color(0x795548) },
  shang: { primary: new THREE.Color(0xffd700), secondary: new THREE.Color(0x7b1fa2) },
  xuan: { primary: new THREE.Color(0x4a148c), secondary: new THREE.Color(0x212121) },
  tie: { primary: new THREE.Color(0x607d8b), secondary: new THREE.Color(0xff5722) },
  yun: { primary: new THREE.Color(0x03a9f4), secondary: new THREE.Color(0xeceff1) },
  neutral: { primary: new THREE.Color(0x888888), secondary: new THREE.Color(0xaaaaaa) },
};

interface ShieldBearerData {
  entity: Entity;
  x: number;
  y: number;
  z: number;
  factionId: FactionId;
  rotation: number;
  speed: number;           // Movement speed for walk animation
  animPhase: number;       // Animation phase offset
  attackTime: number;      // Time when attack started (-1 = not attacking)
  attackDuration: number;  // Duration of attack animation
}

// Vertex shader with attack animation
const shieldBearerVertexShader = `
  attribute vec3 color;
  attribute float animGroup;

  uniform float uTime;

  // Per-instance attributes
  attribute float instanceSpeed;
  attribute float instanceAnimPhase;
  attribute float instanceAttackTime;

  varying vec3 vColor;
  varying vec3 vNormal;
  varying vec3 vWorldPosition;

  // Animation parameters
  const float PI = 3.14159265359;
  const float WALK_FREQUENCY = 4.0;
  const float ATTACK_DURATION = 0.6;  // Attack animation duration in seconds

  vec3 animateVertex(vec3 pos, float group, float time, float speed, float phase, float attackTime) {
    vec3 animated = pos;

    // Calculate attack progress (0 = not attacking, 0-1 = attack progress)
    float attackProgress = 0.0;
    float attackPhase = 0.0;
    if (attackTime >= 0.0) {
      float timeSinceAttack = time - attackTime;
      if (timeSinceAttack < ATTACK_DURATION) {
        attackProgress = timeSinceAttack / ATTACK_DURATION;
        // Attack phases: 0-0.3 = wind up, 0.3-0.5 = thrust, 0.5-1.0 = recover
        if (attackProgress < 0.3) {
          attackPhase = attackProgress / 0.3;  // 0-1 wind up
        } else if (attackProgress < 0.5) {
          attackPhase = 1.0 + (attackProgress - 0.3) / 0.2;  // 1-2 thrust
        } else {
          attackPhase = 2.0 + (attackProgress - 0.5) / 0.5;  // 2-3 recover
        }
      }
    }

    // Walking animation
    float walkIntensity = clamp(speed, 0.0, 1.0);
    float t = time * WALK_FREQUENCY + phase;

    // Idle sway
    float idleSway = sin(time * 1.5 + phase) * 0.002 * (1.0 - walkIntensity);

    if (group < 0.5) {
      // STATIC - no animation
      return animated;
    }

    // Apply idle sway to all animated parts
    animated.z += idleSway;

    if (group < 1.5) {
      // BODY - lean forward during attack, bob during walk
      float walkBob = sin(t * 2.0) * 0.003 * walkIntensity;
      animated.y += walkBob;

      // Attack lean
      if (attackPhase > 0.0) {
        float leanAmount = 0.0;
        if (attackPhase < 1.0) {
          // Wind up - lean back slightly
          leanAmount = -0.01 * attackPhase;
        } else if (attackPhase < 2.0) {
          // Thrust - lean forward aggressively
          float thrustPhase = attackPhase - 1.0;
          leanAmount = -0.01 + 0.03 * thrustPhase;
        } else {
          // Recover - return to neutral
          float recoverPhase = attackPhase - 2.0;
          leanAmount = 0.02 * (1.0 - recoverPhase);
        }
        animated.x += leanAmount;
        animated.y -= abs(leanAmount) * 0.5;
      }
    }
    else if (group < 2.5) {
      // HEAD - follows body with slight delay
      float walkBob = sin(t * 2.0 + 0.2) * 0.002 * walkIntensity;
      animated.y += walkBob;

      if (attackPhase > 0.0) {
        float leanAmount = 0.0;
        if (attackPhase < 1.0) {
          leanAmount = -0.008 * attackPhase;
        } else if (attackPhase < 2.0) {
          float thrustPhase = attackPhase - 1.0;
          leanAmount = -0.008 + 0.025 * thrustPhase;
        } else {
          float recoverPhase = attackPhase - 2.0;
          leanAmount = 0.017 * (1.0 - recoverPhase);
        }
        animated.x += leanAmount;
      }
    }
    else if (group < 3.5) {
      // LEFT_ARM (shield arm) - slight raise during attack
      float walkSwing = sin(t) * 0.008 * walkIntensity;
      animated.x += walkSwing * 0.5;

      if (attackPhase > 0.0) {
        float raiseAmount = 0.0;
        if (attackPhase < 1.0) {
          // Wind up - brace shield
          raiseAmount = 0.005 * attackPhase;
        } else if (attackPhase < 2.0) {
          // Thrust - keep shield braced
          raiseAmount = 0.005;
        } else {
          // Recover
          float recoverPhase = attackPhase - 2.0;
          raiseAmount = 0.005 * (1.0 - recoverPhase);
        }
        animated.y += raiseAmount;
      }
    }
    else if (group < 4.5) {
      // RIGHT_ARM (spear arm) - thrust during attack
      float walkSwing = -sin(t) * 0.008 * walkIntensity;
      animated.x += walkSwing * 0.5;

      if (attackPhase > 0.0) {
        float thrustAmount = 0.0;
        float raiseAmount = 0.0;
        if (attackPhase < 1.0) {
          // Wind up - pull back
          thrustAmount = -0.015 * attackPhase;
          raiseAmount = 0.01 * attackPhase;
        } else if (attackPhase < 2.0) {
          // Thrust - push forward
          float thrustPhase = attackPhase - 1.0;
          thrustAmount = -0.015 + 0.05 * thrustPhase;
          raiseAmount = 0.01 - 0.005 * thrustPhase;
        } else {
          // Recover
          float recoverPhase = attackPhase - 2.0;
          thrustAmount = 0.035 * (1.0 - recoverPhase);
          raiseAmount = 0.005 * (1.0 - recoverPhase);
        }
        animated.x += thrustAmount;
        animated.y += raiseAmount;
      }
    }
    else if (group < 5.5) {
      // LEFT_LEG
      float cycle = t;
      float lift = max(sin(cycle), 0.0) * 0.008 * walkIntensity;
      float swing = sin(cycle) * 0.006 * walkIntensity;
      animated.y += lift;
      animated.x += swing;

      // Brace during attack
      if (attackPhase >= 1.0 && attackPhase < 2.0) {
        animated.x -= 0.003;
      }
    }
    else if (group < 6.5) {
      // RIGHT_LEG
      float cycle = t + PI;
      float lift = max(sin(cycle), 0.0) * 0.008 * walkIntensity;
      float swing = sin(cycle) * 0.006 * walkIntensity;
      animated.y += lift;
      animated.x += swing;

      // Step forward during thrust
      if (attackPhase >= 1.0 && attackPhase < 2.0) {
        float thrustPhase = attackPhase - 1.0;
        animated.x += 0.01 * thrustPhase;
      } else if (attackPhase >= 2.0) {
        float recoverPhase = attackPhase - 2.0;
        animated.x += 0.01 * (1.0 - recoverPhase);
      }
    }
    else if (group < 7.5) {
      // SHIELD - raise and brace during attack
      float walkBob = sin(t * 2.0) * 0.002 * walkIntensity;
      animated.y += walkBob;

      if (attackPhase > 0.0) {
        float raiseAmount = 0.0;
        float braceAmount = 0.0;
        if (attackPhase < 1.0) {
          // Wind up - raise shield
          raiseAmount = 0.015 * attackPhase;
          braceAmount = 0.005 * attackPhase;
        } else if (attackPhase < 2.0) {
          // Thrust - keep raised
          raiseAmount = 0.015;
          braceAmount = 0.005;
        } else {
          // Recover
          float recoverPhase = attackPhase - 2.0;
          raiseAmount = 0.015 * (1.0 - recoverPhase);
          braceAmount = 0.005 * (1.0 - recoverPhase);
        }
        animated.y += raiseAmount;
        animated.z += braceAmount;
      }
    }
    else if (group < 8.5) {
      // SPEAR - thrust forward during attack
      float walkSway = sin(t * 0.5) * 0.003 * walkIntensity;
      animated.z += walkSway;

      if (attackPhase > 0.0) {
        float thrustAmount = 0.0;
        float tiltAmount = 0.0;
        if (attackPhase < 1.0) {
          // Wind up - pull back and raise
          thrustAmount = -0.03 * attackPhase;
          tiltAmount = 0.02 * attackPhase;
        } else if (attackPhase < 2.0) {
          // Thrust - push forward aggressively
          float thrustPhase = attackPhase - 1.0;
          thrustAmount = -0.03 + 0.12 * thrustPhase;
          tiltAmount = 0.02 - 0.03 * thrustPhase;
        } else {
          // Recover
          float recoverPhase = attackPhase - 2.0;
          thrustAmount = 0.09 * (1.0 - recoverPhase);
          tiltAmount = -0.01 * (1.0 - recoverPhase);
        }
        animated.x += thrustAmount;
        animated.y += tiltAmount;
      }
    }
    else if (group < 9.5) {
      // SHOULDER - follows body
      float walkBob = sin(t * 2.0) * 0.002 * walkIntensity;
      animated.y += walkBob;

      if (attackPhase > 0.0) {
        float leanAmount = 0.0;
        if (attackPhase < 1.0) {
          leanAmount = -0.008 * attackPhase;
        } else if (attackPhase < 2.0) {
          float thrustPhase = attackPhase - 1.0;
          leanAmount = -0.008 + 0.02 * thrustPhase;
        } else {
          float recoverPhase = attackPhase - 2.0;
          leanAmount = 0.012 * (1.0 - recoverPhase);
        }
        animated.x += leanAmount;
      }
    }

    return animated;
  }

  void main() {
    vColor = color;
    vNormal = normalize(normalMatrix * normal);

    // Get instance-specific animation parameters
    float speed = instanceSpeed;
    float phase = instanceAnimPhase;
    float attackTime = instanceAttackTime;

    // Animate vertex
    vec3 animatedPosition = animateVertex(position, animGroup, uTime, speed, phase, attackTime);

    vec4 worldPosition = instanceMatrix * vec4(animatedPosition, 1.0);
    vWorldPosition = worldPosition.xyz;

    vec4 mvPosition = modelViewMatrix * worldPosition;
    gl_Position = projectionMatrix * mvPosition;
  }
`;

// Fragment shader with faction colors
const shieldBearerFragmentShader = `
  uniform vec3 primaryColor;
  uniform vec3 secondaryColor;
  uniform vec3 ambientLightColor;
  uniform vec3 directionalLightColor;
  uniform vec3 directionalLightDirection;

  varying vec3 vColor;
  varying vec3 vNormal;
  varying vec3 vWorldPosition;

  void main() {
    vec3 finalColor = vColor;

    // Faction color replacement
    if (vColor.r > 0.9 && vColor.g < 0.2 && vColor.b < 0.2) {
      finalColor = primaryColor;
    } else if (vColor.g > 0.9 && vColor.r < 0.2 && vColor.b < 0.2) {
      finalColor = secondaryColor;
    }

    // Lighting
    vec3 normal = normalize(vNormal);
    vec3 lightDir = normalize(directionalLightDirection);

    float NdotL = dot(normal, lightDir);
    float diff = max(NdotL, 0.0);
    float wrap = max(NdotL * 0.5 + 0.5, 0.0);

    // Rim lighting
    vec3 viewDir = normalize(cameraPosition - vWorldPosition);
    float rim = 1.0 - max(dot(viewDir, normal), 0.0);
    rim = pow(rim, 4.0) * 0.15;

    // Combine lighting
    vec3 ambient = ambientLightColor * 0.55;
    vec3 diffuse = directionalLightColor * diff * 0.35;
    vec3 fill = directionalLightColor * wrap * 0.15;
    vec3 rimColor = vec3(1.0, 0.95, 0.9) * rim;

    vec3 litColor = finalColor * (ambient + diffuse + fill) + rimColor;

    // Slight color variation
    litColor *= 0.95 + 0.1 * fract(sin(dot(vWorldPosition.xz, vec2(12.9898, 78.233))) * 43758.5453);

    gl_FragColor = vec4(litColor, 1.0);
  }
`;

export class ShieldBearerRenderer {
  private scene: THREE.Scene;
  private instancedMeshes: Map<FactionId, THREE.InstancedMesh> = new Map();
  private shieldBearerData: ShieldBearerData[] = [];
  private entityMap: Map<Entity, ShieldBearerData> = new Map();
  private materials: Map<FactionId, THREE.ShaderMaterial> = new Map();
  private dummy = new THREE.Object3D();
  private time = 0;

  // Instance attributes for animation
  private speedAttribute: THREE.InstancedBufferAttribute | null = null;
  private phaseAttribute: THREE.InstancedBufferAttribute | null = null;
  private attackTimeAttribute: THREE.InstancedBufferAttribute | null = null;

  // Event listener cleanup
  private attackEventHandler: ((data: { attackerId: Entity }) => void) | null = null;

  constructor(scene: THREE.Scene) {
    this.scene = scene;
    this.createGeometryAndMeshes();
    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    // Listen for attack events to trigger attack animation
    this.attackEventHandler = (data: { attackerId: Entity }) => {
      const shieldBearer = this.entityMap.get(data.attackerId);
      if (shieldBearer) {
        shieldBearer.attackTime = this.time;
        shieldBearer.attackDuration = 0.6;
      }
    };
    EventBus.on('unit:attack', this.attackEventHandler);
  }

  private createGeometryAndMeshes(): void {
    const geometry = this.createShieldBearerGeometry();

    // Create instance attributes for animation
    const speeds = new Float32Array(MAX_SHIELD_BEARERS);
    const phases = new Float32Array(MAX_SHIELD_BEARERS);
    const attackTimes = new Float32Array(MAX_SHIELD_BEARERS);
    for (let i = 0; i < MAX_SHIELD_BEARERS; i++) {
      speeds[i] = 0;
      phases[i] = Math.random() * Math.PI * 2;
      attackTimes[i] = -1;  // Not attacking
    }

    this.speedAttribute = new THREE.InstancedBufferAttribute(speeds, 1);
    this.phaseAttribute = new THREE.InstancedBufferAttribute(phases, 1);
    this.attackTimeAttribute = new THREE.InstancedBufferAttribute(attackTimes, 1);

    geometry.setAttribute('instanceSpeed', this.speedAttribute);
    geometry.setAttribute('instanceAnimPhase', this.phaseAttribute);
    geometry.setAttribute('instanceAttackTime', this.attackTimeAttribute);

    // Create mesh for each faction
    for (const [factionId, colors] of Object.entries(FACTION_COLORS)) {
      const material = new THREE.ShaderMaterial({
        vertexShader: shieldBearerVertexShader,
        fragmentShader: shieldBearerFragmentShader,
        uniforms: {
          primaryColor: { value: colors.primary },
          secondaryColor: { value: colors.secondary },
          ambientLightColor: { value: new THREE.Color(0xffffff) },
          directionalLightColor: { value: new THREE.Color(0xffffff) },
          directionalLightDirection: { value: new THREE.Vector3(0.5, 1.0, 0.3).normalize() },
          uTime: { value: 0 },
        },
      });
      this.materials.set(factionId as FactionId, material);

      const mesh = new THREE.InstancedMesh(geometry, material, MAX_SHIELD_BEARERS);
      mesh.count = 0;
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      mesh.frustumCulled = false;
      this.instancedMeshes.set(factionId as FactionId, mesh);
      this.scene.add(mesh);
    }
  }

  private createShieldBearerGeometry(): THREE.BufferGeometry {
    const parts: THREE.BufferGeometry[] = [];
    const colors: THREE.Color[] = [];
    const animGroups: number[] = [];

    // === HEAD ===
    const head = new THREE.SphereGeometry(0.09, 8, 6);
    head.translate(0, 0.46, 0);
    parts.push(head);
    colors.push(COLORS.skin);
    animGroups.push(SHIELD_ANIM_GROUP.HEAD);

    // Helmet dome
    const dome = new THREE.SphereGeometry(0.11, 8, 6, 0, Math.PI * 2, 0, Math.PI * 0.55);
    dome.translate(0, 0.52, 0);
    parts.push(dome);
    colors.push(COLORS.shieldBrown);
    animGroups.push(SHIELD_ANIM_GROUP.HEAD);

    // Helmet rim
    const rim = new THREE.TorusGeometry(0.105, 0.012, 4, 12);
    rim.rotateX(Math.PI / 2);
    rim.translate(0, 0.50, 0);
    parts.push(rim);
    colors.push(COLORS.gold);
    animGroups.push(SHIELD_ANIM_GROUP.HEAD);

    // Helmet spike
    const spike = new THREE.ConeGeometry(0.02, 0.04, 6);
    spike.translate(0, 0.60, 0);
    parts.push(spike);
    colors.push(COLORS.gold);
    animGroups.push(SHIELD_ANIM_GROUP.HEAD);

    // Neck guard
    const neckGuard = new THREE.CylinderGeometry(0.10, 0.12, 0.04, 8, 1, true, Math.PI * 0.25, Math.PI * 1.5);
    neckGuard.translate(0, 0.48, 0);
    parts.push(neckGuard);
    colors.push(COLORS.shieldBrown);
    animGroups.push(SHIELD_ANIM_GROUP.HEAD);

    // Face mask
    const faceMask = new THREE.BoxGeometry(0.10, 0.08, 0.02);
    faceMask.translate(0, 0.46, 0.08);
    parts.push(faceMask);
    colors.push(COLORS.shieldBrown);
    animGroups.push(SHIELD_ANIM_GROUP.HEAD);

    // Eye slit
    const eyeSlit = new THREE.BoxGeometry(0.07, 0.015, 0.025);
    eyeSlit.translate(0, 0.48, 0.09);
    parts.push(eyeSlit);
    colors.push(new THREE.Color(0x111111));
    animGroups.push(SHIELD_ANIM_GROUP.HEAD);

    // === TORSO ===
    const chest = new THREE.CylinderGeometry(0.10, 0.12, 0.12, 8);
    chest.translate(0, 0.33, 0);
    parts.push(chest);
    colors.push(COLORS.shieldBrown);
    animGroups.push(SHIELD_ANIM_GROUP.BODY);

    // Chest plate
    const chestPlate = new THREE.BoxGeometry(0.14, 0.10, 0.025);
    chestPlate.translate(0, 0.33, 0.10);
    parts.push(chestPlate);
    colors.push(COLORS.bronze);
    animGroups.push(SHIELD_ANIM_GROUP.BODY);

    // Heart mirror
    const heartMirror = new THREE.CylinderGeometry(0.04, 0.04, 0.012, 12);
    heartMirror.rotateX(Math.PI / 2);
    heartMirror.translate(0, 0.33, 0.115);
    parts.push(heartMirror);
    colors.push(COLORS.gold);
    animGroups.push(SHIELD_ANIM_GROUP.BODY);

    // Heart mirror ring
    const heartMirrorRing = new THREE.TorusGeometry(0.04, 0.008, 6, 16);
    heartMirrorRing.rotateX(Math.PI / 2);
    heartMirrorRing.translate(0, 0.33, 0.115);
    parts.push(heartMirrorRing);
    colors.push(COLORS.bronze);
    animGroups.push(SHIELD_ANIM_GROUP.BODY);

    // Belt
    const belt = new THREE.CylinderGeometry(0.125, 0.125, 0.028, 8);
    belt.translate(0, 0.25, 0);
    parts.push(belt);
    colors.push(COLORS.gold);
    animGroups.push(SHIELD_ANIM_GROUP.BODY);

    // Belt buckle
    const buckle = new THREE.BoxGeometry(0.04, 0.028, 0.015);
    buckle.translate(0, 0.25, 0.12);
    parts.push(buckle);
    colors.push(COLORS.gold);
    animGroups.push(SHIELD_ANIM_GROUP.BODY);

    // Armored skirt
    const skirt = new THREE.CylinderGeometry(0.12, 0.14, 0.07, 8);
    skirt.translate(0, 0.19, 0);
    parts.push(skirt);
    colors.push(COLORS.shieldBrown);
    animGroups.push(SHIELD_ANIM_GROUP.BODY);

    // Shoulder pauldrons
    for (const side of [-1, 1]) {
      const pauldron = new THREE.SphereGeometry(0.05, 6, 5, 0, Math.PI * 2, 0, Math.PI * 0.6);
      pauldron.rotateZ(side * 0.35);
      pauldron.translate(side * 0.13, 0.37, 0);
      parts.push(pauldron);
      colors.push(COLORS.shieldBrown);
      animGroups.push(SHIELD_ANIM_GROUP.SHOULDER);

      const edge = new THREE.TorusGeometry(0.035, 0.007, 4, 8, Math.PI);
      edge.rotateY(Math.PI / 2);
      edge.rotateZ(side * 0.35);
      edge.translate(side * 0.14, 0.34, 0);
      parts.push(edge);
      colors.push(COLORS.gold);
      animGroups.push(SHIELD_ANIM_GROUP.SHOULDER);
    }

    // === LEFT ARM (holding shield) ===
    const leftUpperArm = new THREE.CylinderGeometry(0.025, 0.022, 0.08, 6);
    leftUpperArm.rotateZ(0.8);
    const leftArmX = -0.12 + Math.sin(0.8) * 0.04;
    const leftArmY = 0.35 - Math.cos(0.8) * 0.04;
    leftUpperArm.translate(leftArmX, leftArmY, 0);
    parts.push(leftUpperArm);
    colors.push(COLORS.shieldBrown);
    animGroups.push(SHIELD_ANIM_GROUP.LEFT_ARM);

    const leftForearm = new THREE.CylinderGeometry(0.022, 0.018, 0.07, 6);
    leftForearm.rotateZ(1.4);
    const leftForearmX = -0.12 + Math.sin(0.8) * 0.08 + Math.sin(1.4) * 0.035;
    const leftForearmY = 0.35 - Math.cos(0.8) * 0.08 - Math.cos(1.4) * 0.035;
    leftForearm.translate(leftForearmX, leftForearmY, 0);
    parts.push(leftForearm);
    colors.push(COLORS.shieldBrown);
    animGroups.push(SHIELD_ANIM_GROUP.LEFT_ARM);

    // Forearm extension to shield
    const forearmExtension = new THREE.CylinderGeometry(0.02, 0.018, 0.12, 6);
    forearmExtension.rotateX(Math.PI / 2.5);
    forearmExtension.translate(-0.10, 0.25, 0.07);
    parts.push(forearmExtension);
    colors.push(COLORS.shieldBrown);
    animGroups.push(SHIELD_ANIM_GROUP.LEFT_ARM);

    // === RIGHT ARM (holding spear) ===
    const rightUpperArm = new THREE.CylinderGeometry(0.025, 0.022, 0.08, 6);
    rightUpperArm.rotateZ(-0.4);
    const rightArmX = 0.12 + Math.sin(-0.4) * 0.04;
    const rightArmY = 0.35 - Math.cos(-0.4) * 0.04;
    rightUpperArm.translate(rightArmX, rightArmY, 0);
    parts.push(rightUpperArm);
    colors.push(COLORS.shieldBrown);
    animGroups.push(SHIELD_ANIM_GROUP.RIGHT_ARM);

    const rightForearm = new THREE.CylinderGeometry(0.022, 0.018, 0.07, 6);
    rightForearm.rotateZ(-0.7);
    const rightForearmX = 0.12 + Math.sin(-0.4) * 0.08 + Math.sin(-0.7) * 0.035;
    const rightForearmY = 0.35 - Math.cos(-0.4) * 0.08 - Math.cos(-0.7) * 0.035;
    rightForearm.translate(rightForearmX, rightForearmY, 0);
    parts.push(rightForearm);
    colors.push(COLORS.shieldBrown);
    animGroups.push(SHIELD_ANIM_GROUP.RIGHT_ARM);

    // Right arm extension to spear
    const rightForearmExt = new THREE.CylinderGeometry(0.02, 0.018, 0.10, 6);
    rightForearmExt.rotateZ(-0.3);
    rightForearmExt.rotateX(Math.PI / 4);
    rightForearmExt.translate(0.15, 0.22, 0.04);
    parts.push(rightForearmExt);
    colors.push(COLORS.shieldBrown);
    animGroups.push(SHIELD_ANIM_GROUP.RIGHT_ARM);

    // Right hand grip
    const rightGripHand = new THREE.SphereGeometry(0.025, 6, 5);
    rightGripHand.translate(0.18, 0.20, 0.08);
    parts.push(rightGripHand);
    colors.push(COLORS.woodDark);
    animGroups.push(SHIELD_ANIM_GROUP.RIGHT_ARM);

    // === LEGS ===
    // Left leg
    const leftThigh = new THREE.CylinderGeometry(0.032, 0.028, 0.08, 6);
    leftThigh.rotateZ(0.06);
    leftThigh.translate(-0.045, 0.11, 0);
    parts.push(leftThigh);
    colors.push(COLORS.shieldBrown);
    animGroups.push(SHIELD_ANIM_GROUP.LEFT_LEG);

    const leftShin = new THREE.CylinderGeometry(0.028, 0.024, 0.07, 6);
    leftShin.rotateZ(0.06);
    leftShin.translate(-0.045, 0.035, 0);
    parts.push(leftShin);
    colors.push(COLORS.shieldBrown);
    animGroups.push(SHIELD_ANIM_GROUP.LEFT_LEG);

    const leftFoot = new THREE.BoxGeometry(0.035, 0.02, 0.05);
    leftFoot.translate(-0.045, 0.0, 0.01);
    parts.push(leftFoot);
    colors.push(COLORS.iron);
    animGroups.push(SHIELD_ANIM_GROUP.LEFT_LEG);

    // Left knee guard
    const leftKneeGuard = new THREE.SphereGeometry(0.022, 5, 4, 0, Math.PI * 2, 0, Math.PI * 0.5);
    leftKneeGuard.rotateX(-Math.PI / 2);
    leftKneeGuard.translate(-0.045, 0.08, 0.028);
    parts.push(leftKneeGuard);
    colors.push(COLORS.bronze);
    animGroups.push(SHIELD_ANIM_GROUP.LEFT_LEG);

    // Right leg
    const rightThigh = new THREE.CylinderGeometry(0.032, 0.028, 0.08, 6);
    rightThigh.rotateZ(-0.06);
    rightThigh.translate(0.045, 0.11, 0);
    parts.push(rightThigh);
    colors.push(COLORS.shieldBrown);
    animGroups.push(SHIELD_ANIM_GROUP.RIGHT_LEG);

    const rightShin = new THREE.CylinderGeometry(0.028, 0.024, 0.07, 6);
    rightShin.rotateZ(-0.06);
    rightShin.translate(0.045, 0.035, 0);
    parts.push(rightShin);
    colors.push(COLORS.shieldBrown);
    animGroups.push(SHIELD_ANIM_GROUP.RIGHT_LEG);

    const rightFoot = new THREE.BoxGeometry(0.035, 0.02, 0.05);
    rightFoot.translate(0.045, 0.0, 0.01);
    parts.push(rightFoot);
    colors.push(COLORS.iron);
    animGroups.push(SHIELD_ANIM_GROUP.RIGHT_LEG);

    // Right knee guard
    const rightKneeGuard = new THREE.SphereGeometry(0.022, 5, 4, 0, Math.PI * 2, 0, Math.PI * 0.5);
    rightKneeGuard.rotateX(-Math.PI / 2);
    rightKneeGuard.translate(0.045, 0.08, 0.028);
    parts.push(rightKneeGuard);
    colors.push(COLORS.bronze);
    animGroups.push(SHIELD_ANIM_GROUP.RIGHT_LEG);

    // === SHIELD ===
    const shieldX = -0.11;
    const shieldZ = 0.16;

    // Shield grip
    const shieldGripHorizontal = new THREE.CylinderGeometry(0.012, 0.012, 0.08, 6);
    shieldGripHorizontal.rotateZ(Math.PI / 2);
    shieldGripHorizontal.translate(shieldX, 0.30, shieldZ - 0.03);
    parts.push(shieldGripHorizontal);
    colors.push(COLORS.wood);
    animGroups.push(SHIELD_ANIM_GROUP.SHIELD);

    // Grip mounts
    for (const xOff of [-0.035, 0.035]) {
      const gripMount = new THREE.BoxGeometry(0.015, 0.025, 0.025);
      gripMount.translate(shieldX + xOff, 0.30, shieldZ - 0.018);
      parts.push(gripMount);
      colors.push(COLORS.iron);
      animGroups.push(SHIELD_ANIM_GROUP.SHIELD);
    }

    // Left hand on shield
    const leftGripHand = new THREE.SphereGeometry(0.028, 6, 5);
    leftGripHand.translate(shieldX, 0.30, shieldZ - 0.038);
    parts.push(leftGripHand);
    colors.push(COLORS.woodDark);
    animGroups.push(SHIELD_ANIM_GROUP.SHIELD);

    // Shield body
    const shieldBody = new THREE.BoxGeometry(0.26, 0.35, 0.025);
    shieldBody.translate(shieldX, 0.30, shieldZ);
    parts.push(shieldBody);
    colors.push(COLORS.shieldBrown);
    animGroups.push(SHIELD_ANIM_GROUP.SHIELD);

    // Shield top curve
    const shieldTop = new THREE.CylinderGeometry(0.13, 0.13, 0.025, 8, 1, false, 0, Math.PI);
    shieldTop.rotateX(Math.PI / 2);
    shieldTop.rotateZ(Math.PI / 2);
    shieldTop.translate(shieldX, 0.475, shieldZ);
    parts.push(shieldTop);
    colors.push(COLORS.shieldBrown);
    animGroups.push(SHIELD_ANIM_GROUP.SHIELD);

    // Shield rim
    const rimTop = new THREE.BoxGeometry(0.28, 0.018, 0.03);
    rimTop.translate(shieldX, 0.475, shieldZ);
    parts.push(rimTop);
    colors.push(COLORS.gold);
    animGroups.push(SHIELD_ANIM_GROUP.SHIELD);

    for (const xOff of [-0.13, 0.13]) {
      const rimSide = new THREE.BoxGeometry(0.018, 0.35, 0.03);
      rimSide.translate(shieldX + xOff, 0.30, shieldZ);
      parts.push(rimSide);
      colors.push(COLORS.gold);
      animGroups.push(SHIELD_ANIM_GROUP.SHIELD);
    }

    const rimBottom = new THREE.BoxGeometry(0.28, 0.018, 0.03);
    rimBottom.translate(shieldX, 0.125, shieldZ);
    parts.push(rimBottom);
    colors.push(COLORS.gold);
    animGroups.push(SHIELD_ANIM_GROUP.SHIELD);

    // Shield boss
    const boss = new THREE.SphereGeometry(0.05, 8, 5, 0, Math.PI * 2, 0, Math.PI * 0.5);
    boss.rotateX(-Math.PI / 2);
    boss.translate(shieldX, 0.32, shieldZ + 0.02);
    parts.push(boss);
    colors.push(COLORS.gold);
    animGroups.push(SHIELD_ANIM_GROUP.SHIELD);

    // Tiger head decoration
    const tigerHead = new THREE.CylinderGeometry(0.055, 0.055, 0.01, 12);
    tigerHead.rotateX(Math.PI / 2);
    tigerHead.translate(shieldX, 0.32, shieldZ + 0.015);
    parts.push(tigerHead);
    colors.push(COLORS.gold);
    animGroups.push(SHIELD_ANIM_GROUP.SHIELD);

    // Tiger eyes
    const tigerEyeL = new THREE.SphereGeometry(0.012, 6, 5);
    tigerEyeL.translate(shieldX - 0.03, 0.34, shieldZ + 0.02);
    parts.push(tigerEyeL);
    colors.push(COLORS.bronze);
    animGroups.push(SHIELD_ANIM_GROUP.SHIELD);

    const tigerEyeR = new THREE.SphereGeometry(0.012, 6, 5);
    tigerEyeR.translate(shieldX + 0.03, 0.34, shieldZ + 0.02);
    parts.push(tigerEyeR);
    colors.push(COLORS.bronze);
    animGroups.push(SHIELD_ANIM_GROUP.SHIELD);

    // === SPEAR ===
    const spearX = 0.18;
    const spearZ = 0.08;
    const spearBaseY = 0.20;

    // Spear shaft
    const spearShaft = new THREE.CylinderGeometry(0.012, 0.014, 0.55, 6);
    spearShaft.translate(spearX, spearBaseY + 0.22, spearZ);
    parts.push(spearShaft);
    colors.push(COLORS.wood);
    animGroups.push(SHIELD_ANIM_GROUP.SPEAR);

    // Spear butt
    const spearButt = new THREE.CylinderGeometry(0.016, 0.014, 0.06, 6);
    spearButt.translate(spearX, spearBaseY - 0.08, spearZ);
    parts.push(spearButt);
    colors.push(COLORS.woodDark);
    animGroups.push(SHIELD_ANIM_GROUP.SPEAR);

    // Spear butt cap
    const buttCap = new THREE.CylinderGeometry(0.018, 0.018, 0.015, 8);
    buttCap.translate(spearX, spearBaseY - 0.115, spearZ);
    parts.push(buttCap);
    colors.push(COLORS.iron);
    animGroups.push(SHIELD_ANIM_GROUP.SPEAR);

    // Spear collar
    const spearCollar = new THREE.CylinderGeometry(0.02, 0.016, 0.04, 8);
    spearCollar.translate(spearX, spearBaseY + 0.48, spearZ);
    parts.push(spearCollar);
    colors.push(COLORS.bronze);
    animGroups.push(SHIELD_ANIM_GROUP.SPEAR);

    // Spear head
    const spearHead = new THREE.ConeGeometry(0.025, 0.12, 3);
    spearHead.translate(spearX, spearBaseY + 0.56, spearZ);
    parts.push(spearHead);
    colors.push(COLORS.steel);
    animGroups.push(SHIELD_ANIM_GROUP.SPEAR);

    // Red tassel
    const tassel = new THREE.CylinderGeometry(0.035, 0.01, 0.04, 8);
    tassel.translate(spearX, spearBaseY + 0.45, spearZ);
    parts.push(tassel);
    colors.push(COLORS.tassel);
    animGroups.push(SHIELD_ANIM_GROUP.SPEAR);

    // Merge all geometries with animation group data
    return this.mergeGeometriesWithAnimData(parts, colors, animGroups);
  }

  private mergeGeometriesWithAnimData(
    geometries: THREE.BufferGeometry[],
    colors: THREE.Color[],
    animGroups: number[]
  ): THREE.BufferGeometry {
    // Calculate total vertices
    let totalVertices = 0;
    for (const geo of geometries) {
      const nonIndexed = geo.index ? geo.toNonIndexed() : geo;
      totalVertices += nonIndexed.getAttribute('position').count;
    }

    // Create output arrays
    const positions = new Float32Array(totalVertices * 3);
    const normals = new Float32Array(totalVertices * 3);
    const vertexColors = new Float32Array(totalVertices * 3);
    const animGroupAttr = new Float32Array(totalVertices);

    let offset = 0;
    for (let i = 0; i < geometries.length; i++) {
      let geo = geometries[i];
      if (geo.index) {
        geo = geo.toNonIndexed();
      }

      const posAttr = geo.getAttribute('position');
      const normalAttr = geo.getAttribute('normal');
      const color = colors[i];
      const animGroup = animGroups[i];
      const count = posAttr.count;

      // Add subtle color variation
      const variation = 0.94 + Math.random() * 0.12;

      for (let j = 0; j < count; j++) {
        const idx = offset + j;

        // Position
        positions[idx * 3] = posAttr.getX(j);
        positions[idx * 3 + 1] = posAttr.getY(j);
        positions[idx * 3 + 2] = posAttr.getZ(j);

        // Normal
        if (normalAttr) {
          normals[idx * 3] = normalAttr.getX(j);
          normals[idx * 3 + 1] = normalAttr.getY(j);
          normals[idx * 3 + 2] = normalAttr.getZ(j);
        }

        // Color with variation
        vertexColors[idx * 3] = color.r * variation;
        vertexColors[idx * 3 + 1] = color.g * variation;
        vertexColors[idx * 3 + 2] = color.b * variation;

        // Animation group
        animGroupAttr[idx] = animGroup;
      }

      offset += count;
      geometries[i].dispose();
    }

    // Create merged geometry
    const merged = new THREE.BufferGeometry();
    merged.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    merged.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
    merged.setAttribute('color', new THREE.BufferAttribute(vertexColors, 3));
    merged.setAttribute('animGroup', new THREE.BufferAttribute(animGroupAttr, 1));
    merged.computeVertexNormals();

    return merged;
  }

  addShieldBearer(entity: Entity, x: number, y: number, z: number, factionId: FactionId): void {
    if (this.entityMap.has(entity)) return;

    const data: ShieldBearerData = {
      entity,
      x,
      y,
      z,
      factionId,
      rotation: 0,
      speed: 0,
      animPhase: Math.random() * Math.PI * 2,
      attackTime: -1,
      attackDuration: 0.6,
    };
    this.shieldBearerData.push(data);
    this.entityMap.set(entity, data);
  }

  removeShieldBearer(entity: Entity): void {
    const shieldBearer = this.entityMap.get(entity);
    if (!shieldBearer) return;

    const index = this.shieldBearerData.indexOf(shieldBearer);
    if (index !== -1) {
      const last = this.shieldBearerData[this.shieldBearerData.length - 1];
      this.shieldBearerData[index] = last;
      this.shieldBearerData.pop();
    }
    this.entityMap.delete(entity);
  }

  updateShieldBearer(entity: Entity, x: number, y: number, z: number, rotation?: number): void {
    const shieldBearer = this.entityMap.get(entity);
    if (!shieldBearer) return;

    // Calculate speed from movement
    const dx = x - shieldBearer.x;
    const dz = z - shieldBearer.z;
    const distance = Math.sqrt(dx * dx + dz * dz);

    // Smooth speed transition
    const targetSpeed = Math.min(distance * 30, 1.0);
    shieldBearer.speed = shieldBearer.speed * 0.85 + targetSpeed * 0.15;

    // Update rotation
    if (rotation !== undefined) {
      shieldBearer.rotation = rotation;
    } else if (distance > 0.001) {
      shieldBearer.rotation = Math.atan2(dx, dz);
    }

    shieldBearer.x = x;
    shieldBearer.y = y;
    shieldBearer.z = z;
  }

  update(deltaTime: number): void {
    this.time += deltaTime;

    // Update time uniform for all materials
    for (const material of this.materials.values()) {
      material.uniforms.uTime.value = this.time;
    }

    // Group shield bearers by faction for instanced rendering
    const factionGroups = new Map<FactionId, ShieldBearerData[]>();
    for (const shieldBearer of this.shieldBearerData) {
      if (!factionGroups.has(shieldBearer.factionId)) {
        factionGroups.set(shieldBearer.factionId, []);
      }
      factionGroups.get(shieldBearer.factionId)!.push(shieldBearer);
    }

    // Update each faction's instanced mesh
    for (const [factionId, mesh] of this.instancedMeshes) {
      const shieldBearerList = factionGroups.get(factionId) || [];
      mesh.count = shieldBearerList.length;

      if (shieldBearerList.length === 0) continue;

      // Update instance attributes and matrices
      for (let i = 0; i < shieldBearerList.length; i++) {
        const shieldBearer = shieldBearerList[i];

        // Update instance attributes
        if (this.speedAttribute) {
          this.speedAttribute.setX(i, shieldBearer.speed);
        }
        if (this.phaseAttribute) {
          this.phaseAttribute.setX(i, shieldBearer.animPhase);
        }
        if (this.attackTimeAttribute) {
          this.attackTimeAttribute.setX(i, shieldBearer.attackTime);
        }

        // Update transform matrix
        const surfaceOffset = 0.02;
        this.dummy.position.set(shieldBearer.x, shieldBearer.y + surfaceOffset, shieldBearer.z);
        this.dummy.rotation.set(0, shieldBearer.rotation, 0);
        this.dummy.scale.setScalar(1.75);
        this.dummy.updateMatrix();
        mesh.setMatrixAt(i, this.dummy.matrix);
      }

      // Mark for GPU update
      mesh.instanceMatrix.needsUpdate = true;
      if (this.speedAttribute) this.speedAttribute.needsUpdate = true;
      if (this.phaseAttribute) this.phaseAttribute.needsUpdate = true;
      if (this.attackTimeAttribute) this.attackTimeAttribute.needsUpdate = true;
    }
  }

  getShieldBearerCount(): number {
    return this.shieldBearerData.length;
  }

  clear(): void {
    this.shieldBearerData.length = 0;
    for (const mesh of this.instancedMeshes.values()) {
      mesh.count = 0;
    }
  }

  dispose(): void {
    // Remove event listeners
    if (this.attackEventHandler) {
      EventBus.off('unit:attack', this.attackEventHandler);
    }

    // Dispose meshes
    for (const mesh of this.instancedMeshes.values()) {
      mesh.geometry.dispose();
      if (mesh.material instanceof THREE.Material) {
        mesh.material.dispose();
      }
      this.scene.remove(mesh);
    }
    this.instancedMeshes.clear();
    this.materials.clear();
    this.shieldBearerData.length = 0;
  }
}
